import { useMemo } from 'react';
import { getColorsFromPalette } from '~/_shared/utils/colors/color.palette';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import type { SortedBTGs } from '../boundaryTools.types';
import {
  type ActiveBoundaryFillInfoEntities, useGetBoundaryFillEntities,
} from './useGetBoundaryFillEntities';

export const useBTGFillData = ({
  activeBoundaryTerritoryGroup,
  boundaryTerritoryGroups,
}: {
  activeBoundaryTerritoryGroup: BoundaryTerritoryGroup;
  boundaryTerritoryGroups: SortedBTGs;
}) => {
  const [t] = useTranslation();
  const getBoundaryFillEntities = useGetBoundaryFillEntities();

  const hasMoreThanOneFills = boundaryTerritoryGroups.filter(notNullsy).length > 1;

  const noFillEntities: ActiveBoundaryFillInfoEntities = useMemo(() => ({
    mainEntity: t('BoundaryFill.Manual'),
    shortenedMainEntity: t('BoundaryFill.Manual'),
  }), [t]);

  const allBTGDropDownOptions = boundaryTerritoryGroups
    .map((btg, index) => {
      if (!btg) {
        return null;
      }
      const { background, foreground } = getColorsFromPalette(index);
      const entities = getBoundaryFillEntities(btg) ?? noFillEntities;
      return {
        fillOrderNumber: hasMoreThanOneFills ? index : null,
        backgroundColor: background,
        foregroundColor: foreground,
        boundaryTerritoryGroup: btg,
        entities,
      } as const;
    }).filter(notNullsy);

  const activeBTGOption = allBTGDropDownOptions.find(
    btgOption => btgOption.boundaryTerritoryGroup.boundaryTerritoryGroupId
      === activeBoundaryTerritoryGroup.boundaryTerritoryGroupId
  );
  const filteredBTGDropDownOptions = allBTGDropDownOptions
    .filter(btgOption => btgOption !== activeBTGOption);

  return {
    activeBTGOption,
    filteredBTGDropDownOptions,
  };
};
